import React, { useEffect, useRef, useState } from 'react';

import type {
  GalleryComponent,
  GalleryImageComponent,
} from '../../../../../../core/data-sources/content-components/types.js';

import Image from '../Image.js';
import Arrow from './Arrow.js';
import ViewportChecker from './ViewportChecker.js';

const imageWidth = 584;
const imageSize = {
  width: 780,
  height: 585,
};

interface HorizontalGalleryProps {
  gallery: GalleryComponent;
  isMobile: boolean;
  articleTitle: string;
}

export const HorizontalGallery: React.FC<HorizontalGalleryProps> = ({
  gallery,
  isMobile,
  articleTitle,
}) => {
  const node = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(isMobile ? 0 : 1);
  const [galleryWidth, setGalleryWidth] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) =>
    setTouchEnd(e.targetTouches[0].clientX);

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;

    if (distance < -minSwipeDistance && current > 0) {
      prevSlide();
    } else if (distance > minSwipeDistance && current < length - 1) {
      nextSlide();
    }
  };

  const { components } = gallery;
  const length = components.length;

  const slides = isMobile
    ? components
    : [components[length - 1], ...components.slice(0, length - 1)];

  const nextSlide = () => {
    setCurrent(current === length - 1 ? 0 : current + 1);
  };

  const prevSlide = () => {
    setCurrent(current === 0 ? length - 1 : current - 1);
  };

  useEffect(() => {
    setGalleryWidth(node.current ? node.current.clientWidth : 0);
  }, []);

  const tr = -current * imageWidth + (galleryWidth - imageWidth) / 2;

  const galleryImg = (c: GalleryImageComponent, i: number) => (
    <Image
      {...c}
      articleTitle={articleTitle}
      imageAsset={{ id: c.imageAsset.id, size: imageSize }}
      lazyLoad={i >= 3}
    />
  );

  return (
    <div
      className="gallery"
      ref={node}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {!isMobile && (
        <button onClick={prevSlide} className="left-arrow">
          <Arrow />
        </button>
      )}
      <div
        className="slides"
        style={{
          transform: `translatex(${isMobile ? 0 : tr}px)`,
        }}
      >
        {slides.map((c, i) => {
          const id = c.imageAsset;

          return isMobile ? (
            <ViewportChecker key={`${id}-mb`}>
              {galleryImg(c, i)}
            </ViewportChecker>
          ) : (
            <div className={i === current ? 'active' : 'idle'} key={`${id}-dt`}>
              {galleryImg(c, i)}
            </div>
          );
        })}
      </div>
      {!isMobile && (
        <button className="right-arrow" onClick={nextSlide} id="prev">
          <Arrow />
        </button>
      )}
    </div>
  );
};
